import { type BillingSettings, formatBRL, PRICE_UNITS, type PriceItem, type UpsertPriceItem } from '@dnx/contracts';
import { Alert, Badge, Button, Card, CardHeader, Field, Input, Select, Spinner, Switch } from '@dnx/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { billingSettingsQuery, centsToInput, parseBRL, priceItemsQuery, UNIT_LABELS, UNIT_SHORT } from '../billing/queries.js';
import { api } from '../lib/api.js';
import { SectionHeader } from './layout.js';

function PriceForm({ item, onDone }: { item?: PriceItem; onDone: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ name: item?.name ?? '', description: item?.description ?? '', unit: item?.unit ?? 'fixed', price: item ? centsToInput(item.priceCents) : '', active: item?.active ?? true });
  const cents = parseBRL(f.price);
  const save = useMutation({
    mutationFn: () => {
      const body: UpsertPriceItem = { name: f.name, description: f.description || null, unit: f.unit, priceCents: cents ?? 0, active: f.active };
      return api<PriceItem[]>(item ? `/price-items/${item.id}` : '/price-items', { method: item ? 'PATCH' : 'POST', body });
    },
    onSuccess: (list) => { qc.setQueryData(priceItemsQuery.queryKey, list); onDone(); },
  });
  return (
    <form className="grid gap-4 px-5 py-4 md:grid-cols-[1fr_190px_150px_auto] md:items-end" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
      <Field label="Item"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Ex.: Transplante FUE" maxLength={80} autoFocus /></Field>
      <Field label="Cobrança"><Select value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value as PriceItem['unit'] })}>
        {PRICE_UNITS.map((u) => <option key={u} value={u}>{UNIT_LABELS[u]}</option>)}
      </Select></Field>
      <Field label="Preço (R$)"><Input value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} inputMode="decimal" placeholder="0,00" /></Field>
      <div className="flex gap-1">
        <Button type="submit" disabled={f.name.trim().length < 2 || cents == null} loading={save.isPending}>Salvar</Button>
        <Button variant="ghost" onClick={onDone} aria-label="Cancelar"><X className="size-4" /></Button>
      </div>
      <div className="md:col-span-3"><Field label="Descrição (aparece no orçamento)"><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} maxLength={280} /></Field></div>
      {item && <div className="self-center"><Switch checked={f.active} onChange={(v) => setF({ ...f, active: v })} label="Ativo" /></div>}
      {save.error && <div className="md:col-span-4"><Alert tone="danger">{save.error.message}</Alert></div>}
    </form>
  );
}

function RulesCard({ s }: { s: BillingSettings }) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    maxDiscount: String(s.maxDiscountBp / 100), validityDays: String(s.validityDays), maxInstallments: String(s.maxInstallments),
    cashDiscount: String(s.cashDiscountBp / 100), terms: s.terms ?? '',
  });
  const [saved, setSaved] = useState(false);
  const num = (v: string) => Number(v.replace(',', '.'));
  const save = useMutation({
    mutationFn: () => api<BillingSettings>('/billing/settings', {
      method: 'PUT',
      body: {
        maxDiscountBp: Math.round(num(f.maxDiscount) * 100), validityDays: num(f.validityDays), maxInstallments: num(f.maxInstallments),
        cashDiscountBp: Math.round(num(f.cashDiscount) * 100), terms: f.terms.trim() || null,
      } satisfies BillingSettings,
    }),
    onSuccess: (n) => { qc.setQueryData(billingSettingsQuery.queryKey, n); setSaved(true); },
  });
  const set = (k: keyof typeof f, v: string) => { setF({ ...f, [k]: v }); setSaved(false); };
  return (
    <Card>
      <CardHeader title="Regras dos orçamentos" description="Valem para os próximos orçamentos. Os já enviados não mudam." />
      <form className="grid gap-4 p-5 sm:grid-cols-2 lg:grid-cols-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <Field label="Desconto sem aprovação (até %)" hint="Acima disso, a gestão aprova."><Input value={f.maxDiscount} onChange={(e) => set('maxDiscount', e.target.value)} inputMode="decimal" /></Field>
        <Field label="Desconto à vista no Pix (%)"><Input value={f.cashDiscount} onChange={(e) => set('cashDiscount', e.target.value)} inputMode="decimal" /></Field>
        <Field label="Parcelas no cartão (até)"><Input type="number" min={1} max={24} value={f.maxInstallments} onChange={(e) => set('maxInstallments', e.target.value)} /></Field>
        <Field label="Validade (dias)"><Input type="number" min={1} max={180} value={f.validityDays} onChange={(e) => set('validityDays', e.target.value)} /></Field>
        <div className="sm:col-span-2 lg:col-span-4">
          <Field label="Condições gerais" hint="Aparecem no fim de cada orçamento.">
            <textarea value={f.terms} onChange={(e) => set('terms', e.target.value)} rows={3} maxLength={2000} placeholder="Ex.: Valores válidos para o plano descrito na avaliação. Exames pré-operatórios não inclusos."
              className="w-full resize-y rounded-md border border-border-strong bg-surface px-3 py-2 text-sm text-fg placeholder:text-fg-subtle focus:border-primary" />
          </Field>
        </div>
        {save.error && <div className="sm:col-span-2 lg:col-span-4"><Alert tone="danger">{save.error.message}</Alert></div>}
        <div className="flex items-center gap-3"><Button type="submit" loading={save.isPending}>Salvar regras</Button>{saved && <span className="text-[13px] text-success">Salvo</span>}</div>
      </form>
    </Card>
  );
}

/** Tabela de preços e regras comerciais (gestão e financeiro). */
export function PricesSettingsPage() {
  const { data: items, isLoading } = useQuery(priceItemsQuery);
  const { data: settings } = useQuery(billingSettingsQuery);
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  return (
    <div className="grid gap-6">
      <SectionHeader title="Tabela de preços" description="Itens que a equipe usa para montar orçamentos. Quem orça não altera o preço do item; desconto é à parte e, acima do limite, passa pela gestão."
        actions={editing !== 'new' && <Button onClick={() => setEditing('new')}><Plus className="size-4" />Novo item</Button>} />
      <Card className="overflow-hidden">
        {isLoading ? <Spinner className="m-5 size-5 text-fg-subtle" label="Carregando" /> : (
          <ul className="divide-y divide-border">
            {editing === 'new' && <li><PriceForm onDone={() => setEditing(null)} /></li>}
            {items?.map((it) => (
              <li key={it.id}>
                {editing === it.id ? <PriceForm item={it} onDone={() => setEditing(null)} /> : (
                  <div className="flex items-center gap-4 px-5 py-3.5">
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-2 font-medium text-fg">{it.name}{!it.active && <Badge>Inativo</Badge>}</p>
                      <p className="truncate text-[13px] text-fg-muted">{UNIT_LABELS[it.unit]}{it.description && ` · ${it.description}`}</p>
                    </div>
                    <p className="font-semibold tabular-nums text-fg">{formatBRL(it.priceCents)}<span className="text-[12px] font-normal text-fg-muted">{UNIT_SHORT[it.unit]}</span></p>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(it.id)}><Pencil className="size-4" />Editar</Button>
                  </div>
                )}
              </li>
            ))}
            {!items?.length && editing !== 'new' && <li className="px-5 py-8 text-center text-sm text-fg-muted">Nenhum item ainda. Comece pelo transplante (preço por UF) e os complementos (PRP, exames, kit pós-operatório).</li>}
          </ul>
        )}
      </Card>
      {settings && <RulesCard s={settings} />}
    </div>
  );
}
